import React from "react"
import PropTypes from "prop-types"
import asyncComponent from "asyncComponent"

import withMutation from "store/Mutation/withMutation"

import Text from "./Text"
import Checkbox from "./Checkbox"
import Radio from "./Radio"
import Switch from "./Switch"
import Select from "./Select"
import Number from "./Number"

const COMPONENT_MAP = {
  text: Text,
  checkbox: Checkbox,
  radio: Radio,
  switch: Switch,
  select: Select,
  number: Number,
  markdown: asyncComponent(() => import("./Markdown")),
}

class Field extends React.Component {
  static propTypes = {
    classes: PropTypes.object,

    /**
     * The input component _type_ to render and attach event listeners to.
     */
    component: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),

    /**
     * The mutation to be modified when the user has committed their changes.
     */
    mutation: PropTypes.object.isRequired,

    /**
     * The name of the property on the mutation that will be modified.
     */
    name: PropTypes.string.isRequired,

    /**
     * The default value if the mutation does not have a value. Defaults to the
     * current value of the root object to be changed by the mutation.
     */
    defaultValue: PropTypes.any, // eslint-disable-line react/forbid-prop-types

    /**
     * Immediately update the mutation on every field change. Otherwise only
     * updates on commit.
     */
    immediate: PropTypes.bool,

    /**
     * The field will be rendered with the surrounding text and should inherit
     * the text styling to facilitate in-place editing.
     */
    inPlace: PropTypes.bool,

    /**
     * Don't show error messages from the mutation for the field.
     */
    disableErrorMessage: PropTypes.bool,

    /**
     * Callback invoked each time the value of the field changes.
     */
    onFieldChange: PropTypes.func,

    /**
     * Callback invoked when the user modifications should be flushed and
     * propagated to the mutation.
     */
    onFieldFlush: PropTypes.func,
  }

  static defaultProps = { component: "text" }

  state = {}

  _handleFieldChange = e => {
    this.setState({ pendingChange: e }, () => {
      if (this.props.onFieldChange) {
        this.props.onFieldChange(e)
      }

      if (this.props.immediate) {
        this._handleFieldFlush(e)
      }
    })
  }

  _handleFieldFlush = e => {
    const change = e || this.state.pendingChange
    const { mutation } = this.props

    if (change) {
      mutation.set(this.props.name, change.value)
      this.setState({ pendingChange: undefined }, () => {
        if (this.props.onFieldFlush) {
          this.props.onFieldFlush(change)
        }
      })
    }
  }

  render() {
    const {
      classes,
      immediate,
      component,
      mutation,
      name,
      defaultValue,
      disableErrorMessage,
      ...otherProps
    } = this.props

    let value = this.state.pendingChange
      ? this.state.pendingChange.rawValue
      : mutation.get(name, defaultValue)

    // Force the input component to be 'controlled'.
    if (value === undefined || value === null) {
      value = ""
    }

    const errors = mutation.validationErrors(name)
    const errorProps =
      !disableErrorMessage && errors && errors.length > 0
        ? { error: true, helperText: errors[0].message }
        : {}

    const inputComponent = typeof component === "string" ? COMPONENT_MAP[component] : component

    return React.createElement(inputComponent, {
      ...otherProps,
      ...errorProps,
      value,
      onFieldChange: this._handleFieldChange,
      onFieldFlush: this._handleFieldFlush,
    })
  }
}

export default withMutation()(Field)



// WEBPACK FOOTER //
// ../ui-kit/src/components/ui-Field/Field.js